import { useTranslation } from 'react-i18next'
import type {
  ProductLawApplicabilitySource,
  ProductLawApplicabilityStatus,
} from '@/shared/hooks/api/product'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

function statusVariant(
  status: ProductLawApplicabilityStatus
): 'secondary' | 'warning' | 'success' | 'outline' {
  switch (status) {
    case 'UNDECIDED':
      return 'secondary'
    case 'NEEDS_GRILLING':
      return 'warning'
    case 'APPLICABLE':
      return 'success'
    case 'NOT_APPLICABLE':
      return 'outline'
  }
}

const badgeSizeClassName = {
  default: '',
  lg: 'px-3 py-1 text-sm',
} as const

export function ProductLawApplicabilityBadge({
  status,
  source = null,
  forceReason = null,
  size = 'default',
  className,
}: {
  status: ProductLawApplicabilityStatus
  source?: ProductLawApplicabilitySource | null
  forceReason?: string | null
  size?: keyof typeof badgeSizeClassName
  className?: string
}) {
  const { t } = useTranslation('common')
  const isForced = source === 'USER_FORCE'
  const trimmedReason = forceReason?.trim() ?? ''
  const showReason = isForced && trimmedReason.length > 0
  const badgeClassName = badgeSizeClassName[size]

  return (
    <span className='inline-flex flex-wrap items-center gap-2'>
      <Badge variant={statusVariant(status)} className={cn(badgeClassName, className)}>
        {t(`v2.applicability.status.${status}`)}
      </Badge>
      {isForced && (
        <Badge variant='default' className={badgeClassName}>
          {t('v2.applicability.override.forcedChip')}
        </Badge>
      )}
      {showReason && (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <button
              type='button'
              className={cn(
                'max-w-xs truncate text-muted-foreground',
                size === 'lg' ? 'text-sm' : 'text-xs'
              )}
            >
              {t('v2.applicability.override.reasonInline', {
                reason: trimmedReason,
              })}
            </button>
          </TooltipTrigger>
          <TooltipContent side='top' className='max-w-sm wrap-break-word'>
            {trimmedReason}
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  )
}
